// src/apollo/client.js
// Environment-aware Apollo Client configuration
// Automatically uses localhost in development, production URLs when deployed

import { ApolloClient, InMemoryCache, createHttpLink, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

// ============================================
// ENVIRONMENT-AWARE URL CONFIGURATION
// ============================================
// npm start (development) → localhost:4001
// npm run build / Vercel (production) → api.uniledge.io

const GRAPHQL_HTTP_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.uniledge.io/graphql'
  : 'http://localhost:4001/graphql';

const GRAPHQL_WS_URL = process.env.NODE_ENV === 'production'
  ? 'wss://api.uniledge.io/graphql'
  : 'ws://localhost:4001/graphql';

// Log which environment we're using (helpful for debugging)
if (process.env.NODE_ENV !== 'production') {
  console.log('🔧 Apollo Client running in DEVELOPMENT mode');
  console.log(`   HTTP: ${GRAPHQL_HTTP_URL}`);
  console.log(`   WS:   ${GRAPHQL_WS_URL}`);
}

// ============================================
// HTTP LINK (Queries & Mutations)
// ============================================
const httpLink = createHttpLink({
  uri: GRAPHQL_HTTP_URL,
});

// ============================================
// WEBSOCKET LINK (Subscriptions)
// ============================================
const wsLink = new GraphQLWsLink(
  createClient({
    url: GRAPHQL_WS_URL,
    connectionParams: () => {
      const token = localStorage.getItem('token');
      return token ? { Authorization: token } : {};
    },
    // Reconnection settings
    retryAttempts: 5,
    shouldRetry: () => true,
  })
);

// ============================================
// AUTHENTICATION LINK
// ============================================
const authLink = setContext((_, { headers }) => {
  // Get the token from localStorage - try multiple possible keys
  const token = localStorage.getItem('token') ||
                localStorage.getItem('adminToken') ||
                localStorage.getItem('authToken');

  // Debug logging in development only
  if (process.env.NODE_ENV !== 'production') {
    console.log('Token from localStorage:', token ? `${token.substring(0, 20)}...` : 'null');
  }

  // Return the headers to the context
  return {
    headers: {
      ...headers,
      ...(token ? { authorization: `Bearer ${token}` } : {})
    }
  };
});

// ============================================
// SPLIT LINK (Route by operation type)
// ============================================
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  authLink.concat(httpLink)
);

// ============================================
// APOLLO CLIENT INSTANCE
// ============================================
const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // Merge paginated results for better caching
          products: {
            keyArgs: ['category', 'search'],
            merge(existing = [], incoming) {
              return [...incoming];
            },
          },
          groups: {
            keyArgs: ['status', 'category'],
            merge(existing = [], incoming) {
              return [...incoming];
            },
          },
        },
      },
      User: {
        keyFields: ['id'],
      },
      Product: {
        keyFields: ['id'],
      },
      Group: {
        keyFields: ['id'],
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
  // Connection state tracking
  connectToDevTools: process.env.NODE_ENV !== 'production',
});

export default client;